/* istanbul ignore file */

import { useContext } from 'react';
import { makeStyles } from '@mui/styles';

import Typography from '@classify/react-core-components/Typography';

import LoadMoreButton from '../LoadMoreButton';
import SearchContext from './SearchContext';
import PeopleListItem from './PeopleListItem';
import LoadingSkeleton from './LoadingSkeleton';

const useStyles = makeStyles(({ spacing, palette }) => ({
  root: {
    width: '100%',
  },

  list: {
    listStyle: 'none',
    margin: spacing(2, 0, 3, 0),
    padding: 0,
  },

  listItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${palette.layout.line.divider}`,
    },
  },

  loadMore: {
    marginBottom: spacing(3),
  },

  suggested: {
    marginBottom: spacing(4),
  },
}));

export default function PeopleList({
  translations,
  isLoadMoreDisabled = false,
  suggestedExpert = null,
}) {
  const classes = useStyles();
  const {
    isLoading,
    peopleList,
    total,
    onLoadMore,
    canFetchMore,
    onSelect,
    isFetchingMore,
    query,
    fetchStatus,
    status,
    errorCode,
  } = useContext(SearchContext);

  /* istanbul ignore next */
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!isLoading && total === 0 && !suggestedExpert?.id && !errorCode) {
    return (
      <Typography component="div" variant="subheadingSmall">
        {fetchStatus === 'idle' && status === 'loading'
          ? translations.typeSomething
          : translations.noResults}
      </Typography>
    );
  }

  if (errorCode) {
    return (
      <Typography component="div" variant="subheadingSmall">
        {translations.noResults}
      </Typography>
    );
  }

  const hasQuery = query !== '';

  return (
    <div className={classes.root}>
      {peopleList && (
        <>
          {suggestedExpert && !hasQuery && (
            <div className={classes.suggested} data-testid="suggestedItem">
              <PeopleListItem
                userId={suggestedExpert.id}
                userName={suggestedExpert.displayName}
                subHeading={translations.suggested}
                avatarUrl={suggestedExpert.avatar}
                onClick={() => onSelect(suggestedExpert)}
              />
            </div>
          )}

          <Typography
            component="div"
            sx={{ color: 'text.onMainSecondary' }}
            variant="subheadingSmall"
          >
            {translations.resultCount({ peopleCount: total }, total)}
          </Typography>

          <ul className={classes.list}>
            {peopleList.map((people) => (
              <li key={people.id} className={classes.listItem}>
                <PeopleListItem
                  isSelected={people.isSelected}
                  userId={people.id}
                  userName={people.displayName}
                  subHeading={people.subHeading}
                  avatarUrl={people.avatar}
                  onClick={() => onSelect(people)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {canFetchMore && !isLoadMoreDisabled && (
        <div className={classes.loadMore}>
          <LoadMoreButton onClick={onLoadMore} isLoading={Boolean(isFetchingMore)}>
            {translations.loadMore}
          </LoadMoreButton>
        </div>
      )}
    </div>
  );
}
